import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Sparkles, ShieldCheck, FileSpreadsheet, ArrowRight } from "lucide-react";
import linguisticAuditImage from "@/assets/howitworks/linguistic-audit.png";
import erirReportImage from "@/assets/howitworks/erir-report.png";

function TokenGeneratorPreview() {
  return (
    <div className="flex h-full w-full flex-col justify-between bg-gradient-to-br from-background via-card to-background p-4">
      <div className="flex items-center justify-between text-[10px] font-bold uppercase tracking-[0.18em] text-muted-foreground">
        <span>ORD Sync</span>
        <span className="rounded-full border border-primary/40 bg-primary/10 px-2 py-1 text-primary">live</span>
      </div>
      <div className="space-y-3">
        <div className="h-2 w-2/3 rounded-full bg-muted" />
        <div className="h-2 w-5/6 rounded-full bg-muted/70" />
        <div className="flex items-center gap-2 rounded-lg border border-border/50 bg-background/55 p-3">
          <Sparkles className="h-4 w-4 text-primary" />
          <span className="font-mono text-xs font-bold text-foreground">erid: 2Vtzqv...</span>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2">
        {["ИНН", "Креатив", "Токен"].map((label) => (
          <span key={label} className="rounded-md border border-border/40 bg-card/60 px-2 py-1 text-center text-[10px] font-semibold text-muted-foreground">
            {label}
          </span>
        ))}
      </div>
    </div>
  );
}

const steps = [
  {
    n: "01",
    icon: Sparkles,
    title: "Генерация erid-токена",
    text: "Загрузите креатив — ИИ передаёт данные в ОРД и возвращает идентификатор erid за 2 секунды.",
  },
  {
    n: "02",
    icon: ShieldCheck,
    title: "Лингвистический ИИ-аудит",
    text: "Сканер по ФЗ-168: ловит иностранные слова, проверяет паритет перевода и подсвечивает риски.",
    image: linguisticAuditImage,
  },
  {
    n: "03",
    icon: FileSpreadsheet,
    title: "Подготовка отчёта в ЕРИР",
    text: "Система собирает статусы, токены и готовность отчётности в ЕРИР без ручного ввода и опечаток.",
    image: erirReportImage,
  },
];

export function HowItWorks() {
  return (
    <section
      id="how-it-works"
      aria-labelledby="how-it-works-title"
      className="relative z-10 mx-auto mt-16 w-full max-w-6xl px-4 sm:mt-24"
    >
      <div className="mb-8 text-center sm:mb-12">
        <p className="mb-2 text-xs font-bold uppercase tracking-[0.25em] text-primary">
          Как это работает
        </p>
        <h2
          id="how-it-works-title"
          className="text-2xl font-extrabold tracking-tight text-foreground sm:text-3xl md:text-4xl"
        >
          От креатива до отчёта в&nbsp;ЕРИР — за&nbsp;3&nbsp;шага
        </h2>
        <p className="mx-auto mt-3 max-w-2xl text-sm text-muted-foreground sm:text-base">
          Посмотрите, как ERIDON автоматизирует маркировку, прежде чем регистрироваться.
        </p>
      </div>

      <div className="grid gap-5 sm:grid-cols-2 md:grid-cols-3">
        {steps.map((s, i) => (
          <motion.article
            key={s.n}
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.4, delay: i * 0.08 }}
            className="glass-card group relative flex flex-col overflow-hidden p-5 transition-transform hover:-translate-y-1"
          >
            <div className="mb-4 flex items-center justify-between">
              <span className="text-xs font-mono font-bold tracking-widest text-muted-foreground">
                ШАГ {s.n}
              </span>
              <span className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <s.icon className="h-4 w-4" />
              </span>
            </div>

            <div className="mb-4 aspect-[16/10] w-full overflow-hidden rounded-xl border border-border/40 bg-card/40">
              {s.image ? (
                <img
                  src={s.image}
                  alt={s.title}
                  className="h-full w-full object-cover object-top transition-transform duration-300 group-hover:scale-[1.02]"
                  loading="lazy"
                  decoding="async"
                />
              ) : (
                <TokenGeneratorPreview />
              )}
            </div>

            <h3 className="mb-2 text-base font-bold text-foreground sm:text-lg">{s.title}</h3>
            <p className="text-sm leading-relaxed text-muted-foreground">{s.text}</p>
          </motion.article>
        ))}
      </div>

      <div className="mt-8 flex flex-col items-center gap-3 sm:flex-row sm:justify-center">
        <Link
          to="/pricing"
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl bg-gradient-to-r from-primary to-accent px-6 py-3 text-sm font-semibold text-primary-foreground shadow-lg shadow-primary/30 transition-transform hover:-translate-y-0.5"
        >
          Попробовать бесплатно <ArrowRight className="h-4 w-4" />
        </Link>
        <Link
          to="/demo-dashboard"
          className="inline-flex min-h-[48px] items-center justify-center gap-2 rounded-2xl border border-border/60 bg-background/40 px-6 py-3 text-sm font-semibold text-foreground transition-colors hover:bg-accent"
        >
          Посмотреть демо-кабинет
        </Link>
      </div>
    </section>
  );
}
